import type { UseQueryOptions } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { api, apiOrigin } from '@/lib/api'
import { downloadFile } from '@/lib/download'

export const INVENTORY_REPORT_NAMES = [
  'aging',
  'performance',
  'replenishment',
  'no-sales',
  'stock-value',
] as const

export type InventoryReportName = (typeof INVENTORY_REPORT_NAMES)[number]
/** Every report the shared `reports/{report}/export` endpoint serves from these screens. */
export type InventoryExportName = InventoryReportName | 'stock-valuation'
export type InventoryReportExportFormat = 'csv' | 'pdf'

export interface InventoryReportSearch {
  from?: string
  to?: string
  as_at?: string
  window_days?: number
  review_window_days?: number
  no_sales_window_days?: number
  warehouse_id?: string
  location_id?: string
  variant_id?: string
  product_id?: string
  category_id?: string
  collection_id?: string
  attribute_value_id?: string
  season?: string
  season_name?: string
  view?: 'variant' | 'product'
}

function positiveInteger(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return value > 0 && value % 1 === 0 ? value : undefined
  }

  if (typeof value !== 'string' || !/^\d+$/.test(value)) return undefined

  const parsed = parseInt(value, 10)
  return parsed > 0 ? parsed : undefined
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

export function inventoryReportSearchFrom(search: Record<string, unknown>): InventoryReportSearch {
  return {
    from: nonEmptyString(search.from),
    to: nonEmptyString(search.to),
    as_at: nonEmptyString(search.as_at),
    window_days: positiveInteger(search.window_days),
    review_window_days: positiveInteger(search.review_window_days),
    no_sales_window_days: positiveInteger(search.no_sales_window_days),
    warehouse_id: nonEmptyString(search.warehouse_id),
    location_id: nonEmptyString(search.location_id),
    variant_id: nonEmptyString(search.variant_id),
    product_id: nonEmptyString(search.product_id),
    category_id: nonEmptyString(search.category_id),
    collection_id: nonEmptyString(search.collection_id),
    attribute_value_id: nonEmptyString(search.attribute_value_id),
    season: nonEmptyString(search.season),
    season_name: nonEmptyString(search.season_name),
    view: search.view === 'product' || search.view === 'variant' ? search.view : undefined,
  }
}

export type InventoryReportRow = Record<string, unknown>

export interface InventoryReportPayload {
  columns: string[]
  rows: InventoryReportRow[]
  totals: InventoryReportRow[]
  meta?: {
    report?: string
    calculated_at?: string
    from?: string | null
    to?: string | null
    as_at?: string | null
    currency?: string | null
  }
}

const REPORT_PATHS = {
  aging: '/inventory/reports/aging',
  performance: '/inventory/reports/performance',
  replenishment: '/inventory/reports/replenishment',
  'no-sales': '/inventory/reports/no-sales',
  'stock-value': '/inventory/reports/stock-value',
} as const satisfies Record<InventoryReportName, string>

export const inventoryReportKeys = {
  report: (companyId: string | null, report: InventoryReportName, search: InventoryReportSearch) =>
    ['inventory', companyId, 'reports', report, search] as const,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isRow(value: unknown): value is InventoryReportRow {
  return isRecord(value)
}

function unwrapReport(body: unknown): InventoryReportPayload {
  const resource = isRecord(body) && isRecord(body.data) ? body.data : body

  if (!isRecord(resource)) {
    throw new Error('Inventory report response was not an object.')
  }

  return {
    columns: Array.isArray(resource.columns)
      ? resource.columns.filter((column): column is string => typeof column === 'string')
      : [],
    rows: Array.isArray(resource.rows) ? resource.rows.filter(isRow) : [],
    totals: Array.isArray(resource.totals) ? resource.totals.filter(isRow) : [],
    meta: isRecord(resource.meta)
      ? {
          report: typeof resource.meta.report === 'string' ? resource.meta.report : undefined,
          calculated_at:
            typeof resource.meta.calculated_at === 'string'
              ? resource.meta.calculated_at
              : undefined,
          from: typeof resource.meta.from === 'string' ? resource.meta.from : null,
          to: typeof resource.meta.to === 'string' ? resource.meta.to : null,
          as_at: typeof resource.meta.as_at === 'string' ? resource.meta.as_at : null,
          currency: typeof resource.meta.currency === 'string' ? resource.meta.currency : null,
        }
      : undefined,
  }
}

export function useInventoryReport(
  companyId: string | null,
  report: InventoryReportName,
  search: InventoryReportSearch,
  options?: Omit<UseQueryOptions<InventoryReportPayload, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<InventoryReportPayload, Error>({
    queryKey: inventoryReportKeys.report(companyId, report, search),
    enabled: Boolean(companyId),
    staleTime: 30_000,
    ...options,
    queryFn: async () => {
      if (!companyId) throw new Error('An active company is required to load this report.')

      const response = await api.GET(REPORT_PATHS[report], {
        headers: { 'X-Company-Id': companyId },
        params: { query: search },
      })

      if (response.error) {
        throw new Error('The inventory report request failed.')
      }
      if (!response.data) {
        throw new Error('The inventory report returned no data.')
      }

      return unwrapReport(response.data)
    },
  })
}

function reportQuery(search: object): string {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }

  return params.toString()
}

export function inventoryReportExportUrl(
  report: InventoryExportName,
  format: InventoryReportExportFormat,
  search: object,
): string {
  const query = reportQuery({ ...search })
  const suffix = query ? `?${query}&format=${format}` : `?format=${format}`
  return `${apiOrigin}/api/inventory/reports/${report}/export${suffix}`
}

export async function exportInventoryReport(
  companyId: string,
  report: InventoryExportName,
  format: InventoryReportExportFormat,
  search: object,
): Promise<void> {
  await downloadFile(inventoryReportExportUrl(report, format, search), `${report}.${format}`, {
    'X-Company-Id': companyId,
  })
}
